import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Menu,
  MenuItem,
  Checkbox,
} from "@mui/material";
import { Edit2, Trash2 } from "lucide-react";
import {
  useFetchUnitsQuery,
  useDeleteUnitMutation,
} from "../../../../services/baseQuery";
import { EditUnitModal } from "./UnitComponents/EditUnitModal";
import { DeleteConfirmationModal } from "./UnitComponents/DeleteConfirmationModal";
import { toast } from "react-hot-toast";

export const defaultColumns = [
  { id: "name", label: "Name", minWidth: 140, required: true },
  { id: "abbreviation", label: "Abbreviation", minWidth: 120, required: true },
  { id: "type", label: "Type", minWidth: 120, required: true },
  { id: "category", label: "Category", minWidth: 120, required: false },
  { id: "conversion", label: "Conversion", minWidth: 200, required: false },
  { id: "action", label: "Action", minWidth: 100, required: true },
];

const UnitTable = ({
  searchQuery,
  filters,
  anchorEl,
  onCloseSettings,
  visibleColumns = defaultColumns.reduce(
    (acc, col) => ({ ...acc, [col.id]: true }),
    {},
  ),
  onColumnToggle,
  setRefetchFn,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editUnit, setEditUnit] = useState(null);
  const [deleteUnitId, setDeleteUnitId] = useState(null);

  const {
    data: unitsData,
    isLoading,
    refetch,
  } = useFetchUnitsQuery({
    ...filters,
    search: searchQuery,
  });

  // Set the refetch function for the parent component
  useEffect(() => {
    if (setRefetchFn && refetch) {
      setRefetchFn(() => refetch);
    }
  }, [refetch, setRefetchFn]);

  const [deleteUnit, { isLoading: isDeleting }] = useDeleteUnitMutation();

  const handleDelete = async () => {
    try {
      await deleteUnit(deleteUnitId).unwrap();
      toast.success("Unit deleted successfully");
      setDeleteUnitId(null);
    } catch (error) {
      toast.error(error.message || "Failed to delete unit");
    }
  };

  const visibleColumnsList = defaultColumns.filter(
    column => visibleColumns[column.id],
  );

  const getTypeChipColor = type => {
    switch (type) {
      case "BASE":
        return "bg-blue-100 text-blue-800";
      case "COMPOSITE":
        return "bg-purple-100 text-purple-800";
      case "TERTIARY":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getConversionContent = unit => {
    if (unit.type !== "BASE") {
      if (unit.type === "COMPOSITE") {
        return `1 ${unit.abbreviation} = ${unit.conversionFactor} ${unit.smallestUnit?.name}`;
      } else if (unit.type === "TERTIARY") {
        return `1 ${unit.abbreviation} = ${unit.conversionFactorForLargerUnit} ${unit.largerUnit?.name} = ${unit.conversionFactorForSmallestUnit} ${unit.smallestUnit?.name}`;
      }
    }
    return "-";
  };

  if (isLoading) {
    return (
      <div className="h-1 w-full overflow-hidden">
        <div className="animate-progress bg-[#5652B0] h-1"></div>
      </div>
    );
  }

  // Check if there are no units to display
  const noUnitsFound = !unitsData?.data || unitsData.data.length === 0;

  return (
    <>
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          boxShadow: "none",
          border: "1px solid #E5E7EB",
          borderRadius: "0.5rem",
        }}>
        <TableContainer
          sx={{
            maxHeight: 440,
            minHeight: 200,
          }}>
          <Table stickyHeader aria-label="units table">
            <TableHead>
              <TableRow>
                {visibleColumnsList.map(column => (
                  <TableCell
                    key={column.id}
                    align="center"
                    sx={{
                      minWidth: column.minWidth,
                      backgroundColor: "#F9FAFB",
                      borderBottom: "1px solid #E5E7EB",
                      color: "#374151",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                    }}
                    className="font-outfit">
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {noUnitsFound ? (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumnsList.length}
                    align="center"
                    sx={{
                      py: 8,
                      borderBottom: "1px solid #E5E7EB",
                      color: "#6B7280",
                    }}
                    className="font-outfit">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <svg
                        width="64"
                        height="64"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="text-gray-300">
                        <path
                          d="M10.5 4.5H13.5M10.5 4.5V3M10.5 4.5H6.6C6.26863 4.5 6 4.76863 6 5.1V18.9C6 19.2314 6.26863 19.5 6.6 19.5H17.4C17.7314 19.5 18 19.2314 18 18.9V5.1C18 4.76863 17.7314 4.5 17.4 4.5H13.5M13.5 4.5V3M9 8.25H15M9 11.25H15M9 14.25H12"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <p className="text-base font-medium">No units found</p>
                      {searchQuery ||
                      filters.type !== "ALL" ||
                      filters.category !== "ALL" ? (
                        <p className="text-sm">
                          Try adjusting your filters or search query
                        </p>
                      ) : (
                        <p className="text-sm">
                          Get started by adding your first unit
                        </p>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                unitsData.data
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map(unit => (
                    <TableRow
                      hover
                      key={unit.id}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}>
                      {visibleColumnsList.map(column => {
                        let content;
                        switch (column.id) {
                          case "type":
                            content = (
                              <div
                                className={`px-3 py-1 rounded-full text-sm font-medium inline-block ${getTypeChipColor(
                                  unit.type,
                                )}`}>
                                {unit.type}
                              </div>
                            );
                            break;
                          case "conversion":
                            content = getConversionContent(unit);
                            break;
                          case "action":
                            content = (
                              <div className="flex items-center justify-center gap-2">
                                <IconButton
                                  size="small"
                                  onClick={() => setEditUnit(unit)}
                                  disabled={unit.isSystem}
                                  className="text-gray-600 hover:text-[#5652B0] hover:bg-purple-50">
                                  <Edit2 className="w-4 h-4" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => setDeleteUnitId(unit.id)}
                                  disabled={unit.isSystem || isDeleting}
                                  className="text-gray-600 hover:text-red-600 hover:bg-red-50">
                                  <Trash2 className="w-4 h-4" />
                                </IconButton>
                              </div>
                            );
                            break;
                          default:
                            content = unit[column.id];
                        }

                        return (
                          <TableCell
                            key={column.id}
                            align="center"
                            sx={{
                              borderBottom: "1px solid #E5E7EB",
                              color: "#4B5563",
                              fontSize: "0.875rem",
                            }}
                            className="font-outfit">
                            {content}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={unitsData?.data?.length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: "1px solid #E5E7EB",
            ".MuiTablePagination-select": {
              borderRadius: "0.375rem",
            },
          }}
        />
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onCloseSettings}
        PaperProps={{
          className: "mt-2",
          elevation: 0,
          sx: {
            border: "1px solid #E5E7EB",
            borderRadius: "0.5rem",
            minWidth: 180,
          },
        }}>
        {defaultColumns.map(column => (
          <MenuItem
            key={column.id}
            onClick={() => onColumnToggle?.(column.id)}
            disabled={column.required}
            sx={{ fontSize: "0.875rem" }}>
            <Checkbox
              checked={visibleColumns[column.id]}
              disabled={column.required}
              size="small"
            />
            {column.label}
          </MenuItem>
        ))}
      </Menu>

      {editUnit && (
        <EditUnitModal
          unit={editUnit}
          open={Boolean(editUnit)}
          onClose={() => setEditUnit(null)}
        />
      )}

      {deleteUnitId && (
        <DeleteConfirmationModal
          open={Boolean(deleteUnitId)}
          onClose={() => setDeleteUnitId(null)}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
};

export default UnitTable;
